// Generowanie pisma jako zadanie w tle + odpytywanie (karta KREATOR-ASYNC, 19.10.2026) — WSPÓLNE dla obu UI
// (frontend/src/pages/CreatorPage.jsx i frontend/src/ui-cockpit/views/CreatorView.jsx).
//
// Po co: Cloudflare (tunel do domu) zrywa żądanie HTTP bez pierwszego bajtu odpowiedzi po 100 s (kod 524), a Kreator
// generuje 4–10 min. Za flagą środowiskową KREATOR_ASYNC backend ma `POST /projects/{id}/generate/async` (202 + job_id)
// i `GET /projects/{id}/generate/jobs/{job_id}` (queued/running/done/error). Ten plik:
//   * wie, czy flaga jest włączona (`GET /flags` → `kreator_async`; błąd odczytu = wyłączona, jak klasycznie),
//   * startuje zadanie i odpytuje je co 3 s, aż do `done` (zwraca `{ data }` w kształcie odpowiedzi axios, więc kod
//     obsługi wyniku w UI jest IDENTYCZNY jak dla `/generate`) albo `error` (rzuca błąd w kształcie axios),
//   * błędy sieci i bramy (brak odpowiedzi, 502/504/520–524) w trakcie odpytywania = ponów odpytywanie, NIGDY nie startuj
//     drugi raz; 409 przy starcie (zadanie już biegnie, np. po odświeżeniu strony) = dołącz do trwającego zadania,
//   * gdy trasa async jest niedostępna (404 „Not Found” = flaga wyłączona po stronie serwera) wraca do trasy klasycznej.
// Zero zależności od axios/React (testy bez sieci: wstrzykiwane są start, odczyt, usypianie i zegar).

/** Kody, po których backend mógł dokończyć pracę mimo zerwanego połączenia (Cloudflare: 520–524). */
export const KODY_BRAMY = [502, 504, 520, 521, 522, 523, 524];

/** Błąd bramy/sieci/timeoutu klienta: pismo mogło się zapisać mimo błędu (salvage) albo zadanie nadal trwa. */
export const czyBladBramy = (err) =>
  !err?.response || err?.code === "ECONNABORTED" || KODY_BRAMY.includes(err.response.status);

/** Odpytywanie: ponów po błędzie bramy ORAZ po KAŻDYM 5xx (503 restart/maintenance, 530 Cloudflare przy wyłączonym tunelu). */
export const czyPonowicOdpytywanie = (err) =>
  czyBladBramy(err) || (err.response.status >= 500 && err.response.status <= 599);

export const INTERWAL_ODPYTYWANIA_MS = 3000;
/** Sufit oczekiwania klienta na jedno zadanie (backend ma własny sufit 60 min; generacja trwa 4–10 min). */
export const MAKS_OCZEKIWANIE_MS = 30 * 60 * 1000;

const uspij = (ms) => new Promise((r) => setTimeout(r, ms));

/** Błąd zadania w kształcie błędu axios (`response.status`, `response.data.detail`) — działa z `humanizeApiError`. */
export function bladZadania(blad) {
  const status = Number(blad?.status_code) || 500;
  const detail = blad?.detail;
  const e = new Error(typeof detail === "string" ? detail : `Zadanie generowania zakończone błędem ${status}`);
  e.response = { status, data: { detail } };
  e.zadanieAsync = true;
  return e;
}

/** Odpytuje zadanie do `done`/`error`. `odczytaj()` zwraca odpowiedź axios z ciałem statusu zadania. */
export async function czekajNaZadanie(odczytaj, opcje = {}) {
  const interwal = opcje.interwalMs ?? INTERWAL_ODPYTYWANIA_MS;
  const maks = opcje.maksMs ?? MAKS_OCZEKIWANIE_MS;
  const spij = opcje.uspij ?? uspij;
  const teraz = opcje.teraz ?? Date.now;
  const start = teraz();
  for (;;) {
    await spij(interwal);
    if (teraz() - start > maks) {
      // Klient przestaje czekać; zadanie może jeszcze trwać/zapisać pismo — UI zrobi salvage jak przy timeoucie.
      const e = new Error("timeout: zadanie generowania trwa dłużej niż limit oczekiwania klienta");
      e.code = "ECONNABORTED";
      throw e;
    }
    let res;
    try {
      res = await odczytaj();
    } catch (err) {
      if (czyPonowicOdpytywanie(err)) continue; // przerwa/524/502/503/530: zadanie biegnie dalej, pytamy ponownie
      if (err?.response?.status === 410) {
        // Wynik już odebrany (odpowiedź z wynikiem mogła zgubić się w drodze): pismo jest zapisane w projekcie,
        // więc zgłaszamy to jak timeout bramy — UI zrobi salvage przez getDocument.
        const e = new Error("wynik zadania został już odebrany");
        e.code = "ECONNABORTED";
        throw e;
      }
      throw err; // 401/403/404 (zadanie wygasło)/422 itd. — prawdziwy błąd
    }
    const d = res?.data || {};
    if (d.postep && opcje.przyPostepie) {
      try { opcje.przyPostepie(d.postep); } catch { /* postęp jest tylko ozdobą */ }
    }
    if (d.status === "done") return { data: d.wynik };
    if (d.status === "error") throw bladZadania(d.blad);
    // queued / running / nieznany status: czekamy dalej
  }
}

/** Rodzaje zadań Kreatora (ASYNC-2): jeden „slot” na projekt, wspólny GET statusu; pole `rodzaj` w statusie i w 409. */
export const RODZAJE_ZADAN = ["generate", "iterate", "auto_fix"];
const OPISY_RODZAJOW = { generate: "generowanie pisma", iterate: "poprawianie pisma", auto_fix: "auto-poprawka" };

/** JSON kanoniczny (klucze obiektów posortowane) — do porównywania payloadów startu. */
export function kanonicznyJson(v) {
  if (Array.isArray(v)) return `[${v.map(kanonicznyJson).join(",")}]`;
  if (v && typeof v === "object") {
    return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${kanonicznyJson(v[k])}`).join(",")}}`;
  }
  return JSON.stringify(v === undefined ? null : v);
}

/**
 * Pamięć modułu: ostatni payload, z którym TA karta przeglądarki UDANIE wystartowała zadanie (202) — per projekt + rodzaj.
 * Backend nie zwraca payloadu trwającego zadania, więc to jedyny sposób, by przy 409 sprawdzić, czy trwa zadanie z TYMI
 * SAMYMI danymi (job_id musi się zgadzać, żeby stary wpis nie pasował do cudzego/nowszego zadania).
 */
const pamiecPayloadow = new Map();
const kluczPamieci = (klucz, rodzaj) => `${klucz}|${rodzaj}`;

/** Do testów. */
export function wyczyscPamiecPayloadow() {
  pamiecPayloadow.clear();
}

export const KOMUNIKAT_INNE_UWAGI =
  "Trwa poprawka pisma z innymi uwagami (albo rozpoczęta wcześniej) — poczekaj na jej zakończenie. Twoje uwagi nie zostały usunięte.";

/**
 * Start zadania; zwraca job_id albo `null`, gdy trasa async jest niedostępna (serwer: flaga wyłączona).
 * 409 z job_id tego samego `rodzaju` = dołącz do trwającego; 409 z innym rodzajem (np. trwa generowanie, a klikamy
 * „Popraw”) NIE dołącza (wynik byłby innego rodzaju niż oczekuje UI) — czytelny komunikat zamiast tego.
 * `weryfikacja` = { klucz, payload } (opcjonalna): dołączenie tylko, gdy trwa zadanie wystartowane z IDENTYCZNYM payloadem
 * (JSON kanoniczny); inaczej błąd `KOMUNIKAT_INNE_UWAGI` (UI nie czyści wtedy pola uwag).
 */
async function uruchom(start, rodzaj = "generate", weryfikacja = null) {
  try {
    const res = await start();
    const jobId = res?.data?.job_id;
    if (!jobId) throw new Error("Serwer nie zwrócił identyfikatora zadania generowania.");
    if (weryfikacja) {
      pamiecPayloadow.set(kluczPamieci(weryfikacja.klucz, rodzaj), { jobId, json: kanonicznyJson(weryfikacja.payload) });
    }
    return jobId;
  } catch (err) {
    const r = err?.response;
    if (r?.status === 404 && r?.data?.detail === "Not Found") return null; // flaga wyłączona po stronie serwera
    if (r?.status === 409) {
      const szczegol = r?.data?.detail;
      const trwajacy = szczegol?.rodzaj || "generate"; // serwer sprzed ASYNC-2 nie podaje rodzaju: były tylko generacje
      const ten_sam = szczegol?.job_id && trwajacy === rodzaj;
      if (ten_sam && !weryfikacja) return szczegol.job_id; // już trwa (odświeżona strona, drugi klik) — dołącz
      if (ten_sam) {
        const zapamietane = pamiecPayloadow.get(kluczPamieci(weryfikacja.klucz, rodzaj));
        if (zapamietane && zapamietane.jobId === szczegol.job_id && zapamietane.json === kanonicznyJson(weryfikacja.payload)) {
          return szczegol.job_id; // to nasze zadanie z tymi samymi danymi (np. drugi klik) — dołącz
        }
        err.response = { ...r, data: { detail: KOMUNIKAT_INNE_UWAGI } };
        throw err;
      }
      const komunikat = szczegol?.job_id && trwajacy !== rodzaj
        ? `W tym projekcie trwa już inne zadanie Kreatora (${OPISY_RODZAJOW[trwajacy] || trwajacy}). Poczekaj na jego zakończenie.`
        : szczegol?.message || "Zadanie Kreatora w tym projekcie już trwa.";
      err.response = { ...r, data: { detail: komunikat } };
    }
    throw err;
  }
}

let pamiecFlagi = null;

/** Czy generować przez trasy async (flaga z `GET /flags`). Błąd odczytu = false (klasycznie) i nie jest zapamiętywany. */
export async function czyKreatorAsync(pobierzFlagi) {
  if (pamiecFlagi !== null) return pamiecFlagi;
  try {
    const res = await pobierzFlagi();
    pamiecFlagi = res?.data?.kreator_async === true;
    return pamiecFlagi;
  } catch {
    return false;
  }
}

/** Do testów i do wymuszenia ponownego odczytu flagi. */
export function ustawPamiecFlagiKreatorAsync(wartosc) {
  pamiecFlagi = wartosc;
}

/**
 * Generuje pismo: gdy `asyncWlaczone` — jako zadanie w tle z odpytywaniem; inaczej (albo gdy trasa async jest
 * niedostępna) `klasycznie()`. Zwraca `{ data }` jak `generateDocument` / `iterateDocument` / `autoFixDocument`.
 * `rodzaj` (domyślnie "generate"): "generate" | "iterate" | "auto_fix" — patrz `uruchom`.
 * `klucz` (id projektu) + `payload` (to, co idzie w ciele startu): włączają dołączanie do trwającego zadania tylko przy
 * identycznym payloadzie (używa tego iterate; bez nich 409 tego samego rodzaju dołącza jak dotąd).
 */
export async function generujPismo({ asyncWlaczone, start, odczytaj, klasycznie, rodzaj = "generate", klucz, payload, ...opcje }) {
  if (!asyncWlaczone) return klasycznie();
  const jobId = await uruchom(start, rodzaj, klucz !== undefined && payload !== undefined ? { klucz, payload } : null);
  if (jobId === null) {
    pamiecFlagi = false;
    return klasycznie();
  }
  return czekajNaZadanie(() => odczytaj(jobId), opcje);
}
